import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Footer from '../../components/Footer';
import PageHeader from '../../components/PageHeader';
import usePetSingle from '../../hooks/usePetSingle';
import LightBoxGallery from '../../components/LightBoxGallery';
import { calculateAge } from '../../utils/calculateAge';
import ReactMarkdown from 'react-markdown';
import './PetDetailPage.css';

const minHeight = 450;

function PetDetailPage() {
  const { petSlug } = useParams();
  const { t, i18n } = useTranslation();

  const { data: pet, isLoading, error } = usePetSingle(petSlug);

  const placeholder = '/img/dogimage404-funny.png';

  const galleryItems =
    Array.isArray(pet?.images) && pet.images.length > 0
      ? pet.images.map((imgSrc, idx) => ({
        src: imgSrc && imgSrc.trim() !== '' ? imgSrc : placeholder,
        title: `Image ${idx + 1}`,
      }))
      : [
        {
          src: placeholder,
          title: 'No images available',
        },
      ];

  const getFeatureByGender = (baseText, gender) => {
    // Radi samo za bosanski jezik
    if (i18n.language !== 'bs') return baseText;

    if (!gender) return baseText;

    // muški rod → standardno
    if (gender.toLowerCase() === 'male') {
      return baseText;
    }

    // ženski rod → "Dobar" -> "Dobra"
    if (gender.toLowerCase() === 'female') {
      return baseText.replace(/^Dobar/, 'Dobra');
    }

    return baseText;
  };

  const featuresArray = [
    pet?.good_with_kids &&
    getFeatureByGender(t('common.features.good_with_kids'), pet.gender),

    pet?.good_with_dogs &&
    getFeatureByGender(t('common.features.good_with_dogs'), pet.gender),

    pet?.good_with_cats &&
    getFeatureByGender(t('common.features.good_with_cats'), pet.gender),
  ].filter(Boolean);

  // Helper to format yes/no fields from your data ("da" means yes)
  const formatYesNo = val => {
    if (val === true) return t('common.yes');
    if (val === false) return t('common.no');
    return '-';
  };

  return (
    <div id="top">
      <PageHeader
        title={
          pet
            ? `${t('adoptionPage.adopt')} ${pet.name}`
            : t('adoptionPage.adopt_pet')
        }
        page={t('adoptionPage.adopt_pet')}
        pageUrl={'adoption'}
        subPage={pet ? `${t('adoptionPage.adopt')} ${pet.name}` : ''}
      />

      <div className="page">
        {isLoading ? (
          <div className="text-center">
            <div className="spinner-border" role="status">
              <span className="sr-only" />
            </div>
            <p>Loading...</p>
          </div>
        ) : error ? (
          <div className="container">
            <h2>{t('common.error')}</h2>
          </div>
        ) : !pet ? (
          <div className="container">
            <h2>{t('common.pageNotFound')}</h2>
          </div>
        ) : (
          <div className="container">
            <div className="row">
              <div className="col-lg-12">
                <div
                  className="row bg-light-custom border-irregular1 block-padding align-items-center"
                  style={{ minHeight }}
                >
                  {/* Images carousel */}
                  <div className="col-lg-6 offset-lg-1 ">
                    {Array.isArray(galleryItems) &&
                      galleryItems.length === 1 ? (
                      // Ako postoji samo jedna slika — obični <img>
                      <img
                        src={
                          galleryItems[0].src &&
                            galleryItems[0].src.trim() !== ''
                            ? galleryItems[0].src
                            : placeholder
                        }
                        alt={pet?.name}
                        className="border-irregular1 img-fluid hover-opacity"
                        style={{ maxHeight: '450px', objectFit: 'cover' }}
                      />
                    ) : (
                      <LightBoxGallery
                        images={galleryItems}
                        title={pet?.name}
                        maxMain={1}
                      />
                    )}
                  </div>

                  {/* Pet info */}
                  <div className="col-lg-5 res-margin mt-5 text-xs-center">
                    <h4>
                      <strong>{t('adoptionPage.name')}:</strong> {pet.name}
                    </h4>
                    <div className="row">
                      <div className="col-sm-6">
                        <ul className="list-unstyled pet-adopt-info">
                          <li className="h7">
                            {t('adoptionPage.gender')}:{' '}
                            <span>
                              {t(`common.gender.${pet.gender?.toLowerCase()}`)}
                            </span>
                          </li>
                          <li className="h7">
                            {t('adoptionPage.age')}:{' '}
                            <span>{calculateAge(pet.birth_date, t)}</span>
                          </li>
                          <li className="h7">
                            {t('adoptionPage.size')}:{' '}
                            <span>
                              {t(
                                `common.size.${pet.size_category?.toLowerCase()}`
                              )}
                            </span>
                          </li>
                          {/* <li className="h7">
                            {!isLoading && pet?.status === 'reserved' && (
                              <span className="bg-warning text-dark">
                                {t('adoptionPage.reserved')}
                              </span>
                            )}
                          </li> */}
                        </ul>
                      </div>
                      <div className="col-sm-6">
                        <ul className="list-unstyled pet-adopt-info">
                          <li className="h7">
                            {t('adoptionPage.sterilised')}:{' '}
                            <span>{formatYesNo(pet.neutered)}</span>
                          </li>
                          <li className="h7">
                            {t('adoptionPage.vaccinated')}:{' '}
                            <span>{formatYesNo(pet.vaccinated)}</span>
                          </li>
                        </ul>
                      </div>
                    </div>
                    <ReactMarkdown
                      components={{
                        a: ({ node, ...props }) => (
                          <a
                            {...props}
                            target="_blank"
                            rel="noopener noreferrer"
                          />
                        ),
                      }}
                    >
                      {i18n.language === 'en'
                        ? pet?.about_en || pet?.about
                        : pet?.about}
                    </ReactMarkdown>
                    <div className="pet-sponsors-container">
                      <strong className="pet-sponsors-title">
                        {t('adoptionPage.sponsors')}:
                      </strong>
                      {pet.sponsors && pet.sponsors.length > 0 ? (
                        <div className="pet-sponsors-list">
                          {pet.sponsors
                            .slice() // kopija niza
                            .sort((a, b) => {
                              const order = ['gold', 'silver', 'bronze'];
                              return (
                                order.indexOf(a.sponsor_type.toLowerCase()) -
                                order.indexOf(b.sponsor_type.toLowerCase())
                              );
                            })
                            .map((sponsor, index) => {
                              const type = sponsor.sponsor_type.toLowerCase();
                              const iconSrc =
                                type === 'gold'
                                  ? '/img/sponsor/gold_sponsor1.webp'
                                  : type === 'silver'
                                    ? '/img/sponsor/silver_sponsor.webp'
                                    : '/img/sponsor/bronze_sponsor.webp';

                              const nameColor =
                                type === 'gold'
                                  ? '#d4af37'
                                  : type === 'silver'
                                    ? '#757575'
                                    : '#cd7f32';

                              return (
                                <div
                                  key={index}
                                  className={`pet-sponsor-badge sponsor-${type}`}
                                >
                                  <img
                                    src={iconSrc}
                                    alt={`${sponsor.sponsor_type} sponsor`}
                                    className="pet-sponsor-badge-icon"
                                  />
                                  <span
                                    className="pet-sponsor-badge-name"
                                    style={{ color: nameColor }}
                                  >
                                    {sponsor.name}
                                  </span>
                                </div>
                              );
                            })}
                        </div>
                      ) : (
                        <span>
                          {t('adoptionPage.no_sponsors')}
                        </span>
                      )}
                    </div>

                    {pet?.status === 'reserved' || pet?.status === 'adopted' ? (
                      <button className="btn btn-secondary text-black" disabled>
                        {pet?.status === 'adopted'
                          ? i18n.language === 'en'
                            ? 'This dog is adopted'
                            : 'Ovaj pas je udomljen'
                          : i18n.language === 'en'
                            ? 'This dog is reserved'
                            : 'Ovaj pas je rezervisan'}
                      </button>
                    ) : (
                      <Link
                        className="btn btn-primary"
                        to={
                          i18n.language === 'bs'
                            ? `/adoption-form?petName=${pet.name}`
                            : `/${i18n.language}/adoption-form?petName=${pet.name}`
                        }
                      >
                        {t('adoptionPage.contact_us_to_adopt')}
                      </Link>
                    )}
                  </div>
                </div>

                {/* About section */}
                <div className="col-md-12 mt-5">
                  <h3>
                    {t('adoptionPage.more_about')} {pet.name}
                  </h3>
                  <ul className="custom list-inline font-weight-bold">
                    {featuresArray.map((feature, index) => (
                      <li className="list-inline-item" key={index}>
                        {feature}
                      </li>
                    ))}
                  </ul>
                  <ReactMarkdown
                    components={{
                      a: ({ node, ...props }) => (
                        <a
                          {...props}
                          target="_blank"
                          rel="noopener noreferrer"
                        />
                      ),
                    }}
                  >
                    {i18n.language === 'en'
                      ? pet?.description_en || pet?.description
                      : pet?.description}
                  </ReactMarkdown>

                  <div className="alert alert-primary mt-5" role="alert">
                    <h6 className="text-light">
                      {t('adoptionPage.adoption_rules')}
                    </h6>

                    <p>{t('adoptionPage.adoption_rules_title')}</p>

                    <ReactMarkdown
                      components={{
                        p: ({ node, ...props }) => <p {...props} />,
                        strong: ({ node, ...props }) => <strong {...props} />,
                      }}
                    >
                      {t('adoptionPage.adoption_rules_combined')}
                    </ReactMarkdown>
                  </div>

                  <p className="custom-link">
                    <Link
                      to={
                        i18n.language === 'bs'
                          ? '/adoption'
                          : `/${i18n.language}/adoption`
                      }
                    >
                      {t('adoptionPage.go_back')}
                    </Link>
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
}

export default PetDetailPage;
